import { createFileRoute, Link } from "@tanstack/react-router";
import { gatedRobotsMeta } from "@/lib/seo-gating";
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, LifeBuoy, RefreshCw, Star } from "lucide-react";
import {
  DEPRESSION_PATTERNS,
  DEPRESSION_TOOLS,
  DISCLAIMER,
  DONT_KNOW_SUGGESTIONS,
  NEED_ROUTER,
  TOOL_BY_SLUG,
} from "@/data/depression";
import { useDepressionHub } from "@/hooks/useDepressionHub";
import { DepressionCheckIn } from "@/components/depression/DepressionCheckIn";
import {
  AnchorsTool,
  BodyNeedsTool,
  BodySymptomTool,
  ConnectionTool,
  CycleTool,
  EmotionWheelTool,
  MeaningTool,
  MotivationTool,
  PatternsTool,
  PleasureExperimentTool,
  ThoughtReframeTool,
  TinyStepTool,
} from "@/components/depression/DepressionTools";
import {
  BodyAndMood,
  GroundingShortcuts,
  SleepAndMood,
  SymptomsExplorer,
  TreatmentSupport,
  TriggersExplorer,
  UnderstandingDepression,
} from "@/components/depression/DepressionEducation";
import { LinkButton, Microcopy, PrivateBadge, SoftButton, ToolCard } from "@/components/depression/shared";
import { DepressionCarousel } from "@/components/depression/DepressionCarousel";
import { DepressionReflections } from "@/components/depression/DepressionReflections";

const TITLE = "Depression — Understand, Check In, Take One Small Step · Recovery Trauma™";
const DESCRIPTION =
  "An interactive, trauma-informed depression space: a reflective check-in, symptom and trigger explorers, tiny steps, thought reframes, mood experiments and gentle body tools. Educational, never diagnostic.";

export const Route = createFileRoute("/learn/depression")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://recoverytrauma.com/learn/depression" },
      { name: "twitter:card", content: "summary_large_image" },
      ...gatedRobotsMeta("/learn/depression"),
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/learn/depression" }],
  }),
  component: DepressionHubPage,
});

const INTERNAL_TOOLS = new Set([
  "tiny-step",
  "meaning",
  "pleasure-experiment",
  "thought-reframe",
  "connection",
  "anchors",
  "patterns",
  "body-needs",
  "body-symptoms",
  "emotion-wheel",
  "cycle",
  "motivation",
]);

function DepressionHubPage() {
  const hub = useDepressionHub();
  const [openTool, setOpenTool] = useState<string | null>(null);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [favourites, setFavourites] = useState<string[]>([]);
  const toolRef = useRef<HTMLDivElement | null>(null);
  const checkInRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("rt:depression:toolbox");
      if (raw) setFavourites(JSON.parse(raw));
    } catch {
      /* ignore */
    }
  }, []);

  function toggleFavourite(slug: string) {
    setFavourites((prev) => {
      const next = prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug];
      try {
        localStorage.setItem("rt:depression:toolbox", JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  function openTheTool(slug: string) {
    const tool = TOOL_BY_SLUG[slug];
    if (tool?.href) {
      window.location.href = tool.href;
      return;
    }
    setOpenTool(slug);
    requestAnimationFrame(() => toolRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }

  const latest = hub.checkins[0];
  const recentEntry = hub.entries[0];

  const personalised = useMemo(() => {
    if (!latest?.top_patterns?.length) return [];
    const slugs: string[] = [];
    for (const p of latest.top_patterns) {
      for (const s of DEPRESSION_PATTERNS[p]?.suggests ?? []) {
        if (!slugs.includes(s)) slugs.push(s);
      }
    }
    return slugs.slice(0, 3);
  }, [latest]);

  const toolProps = { hub, onOpenTool: openTheTool };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-24 pt-6 sm:px-6">
      <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden /> Back to Understand
      </Link>

      <header className="mt-4">
        <h1 className="font-serif text-3xl leading-tight text-foreground sm:text-4xl">Depression</h1>
        <p className="mt-2 text-base text-foreground/80">
          Understand what may be happening — emotionally, physically and behaviourally.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-foreground/70">
          Depression can affect far more than mood. It may influence your energy, motivation,
          concentration, sleep, appetite, relationships, thoughts and the way your body feels.
        </p>
        <p className="mt-2 text-sm leading-relaxed text-foreground/70">
          This space is designed to help you understand what you may be experiencing, recognise
          patterns and explore supportive tools one small step at a time.
        </p>
        <p className="mt-3 rounded-2xl border border-border/70 bg-cream/40 p-3 text-xs leading-relaxed text-muted-foreground dark:bg-cream/10">
          {DISCLAIMER}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <SoftButton
            variant="primary"
            onClick={() => {
              setCheckInOpen(true);
              requestAnimationFrame(() =>
                checkInRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
              );
            }}
          >
            Check in with how I feel
          </SoftButton>
          <SoftButton
            onClick={() =>
              document.getElementById("need-now")?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          >
            I need something right now
          </SoftButton>
        </div>
      </header>

      {/* Welcome back / recent activity */}
      {latest || recentEntry ? (
        <section className="mt-6 rounded-3xl border border-gold/30 bg-card/70 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-serif text-xl text-foreground">Welcome back</h2>
            <PrivateBadge />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">Continue whenever you're ready.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <SoftButton onClick={() => setCheckInOpen(true)}>Repeat your check-in</SoftButton>
            <SoftButton onClick={() => openTheTool("anchors")}>Your saved anchors</SoftButton>
            <SoftButton onClick={() => openTheTool("pleasure-experiment")}>
              Try an activity that helped before
            </SoftButton>
            <LinkButton to="/check-in">View mood patterns</LinkButton>
          </div>
        </section>
      ) : null}

      {/* Personalised suggestions */}
      {personalised.length ? (
        <section className="mt-6 rounded-3xl border border-border/70 bg-background/60 p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
            Based on what you told us in your last check-in
          </p>
          <p className="mt-1 text-sm text-foreground/80">
            You reported {latest?.top_patterns.map((p) => DEPRESSION_PATTERNS[p]?.label.toLowerCase()).join(", ")}{" "}
            most strongly. You may want to start with:
          </p>
          <ol className="mt-3 flex flex-wrap gap-2">
            {personalised.map((slug, i) => {
              const t = TOOL_BY_SLUG[slug];
              if (!t) return null;
              return (
                <li key={slug}>
                  {t.href ? (
                    <LinkButton to={t.href}>
                      {i + 1}. {t.emoji} {t.title}
                    </LinkButton>
                  ) : (
                    <SoftButton onClick={() => openTheTool(slug)}>
                      {i + 1}. {t.emoji} {t.title}
                    </SoftButton>
                  )}
                </li>
              );
            })}
          </ol>
          <Microcopy>You don't need to complete all three. Choose one.</Microcopy>
        </section>
      ) : null}

      {/* Check-in */}
      <div ref={checkInRef} className="mt-6 scroll-mt-24">
        <h2 className="font-serif text-2xl text-foreground">How Have You Been Feeling Lately?</h2>
        <p className="mt-1 text-sm text-foreground/80">
          Take a moment to notice what has been happening recently. There are no right or wrong
          answers.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">This is a reflective check-in, not a diagnosis.</p>
        <div className="mt-4">
          {checkInOpen ? (
            hub.signedIn === false ? (
              <div className="rounded-3xl border border-border/70 bg-card/70 p-5">
                <p className="text-sm text-foreground/80">
                  Sign in to save your check-in privately and notice changes over time.
                </p>
                <div className="mt-3">
                  <LinkButton to="/auth" variant="primary">
                    Sign in
                  </LinkButton>
                </div>
              </div>
            ) : (
              <DepressionCheckIn hub={hub} onOpenTool={openTheTool} />
            )
          ) : (
            <SoftButton variant="primary" onClick={() => setCheckInOpen(true)}>
              Start the check-in
            </SoftButton>
          )}
        </div>
      </div>

      {/* Action router */}
      <section id="need-now" className="mt-10 scroll-mt-24">
        <h2 className="font-serif text-2xl text-foreground">What Do You Need Right Now?</h2>
        <p className="mt-1 text-sm text-foreground/80">
          You don't have to figure everything out. Choose the one that feels closest.
        </p>
        <div className="mt-4">
          <DepressionCarousel label="What do you need right now">
          {NEED_ROUTER.map((n) => {
            const t = TOOL_BY_SLUG[n.tool];
            const content = (
              <>
                <span aria-hidden className="text-xl">{n.emoji}</span>
                <span className="text-sm font-medium">{n.label}</span>
              </>
            );
            const cls =
              "flex h-full min-h-[88px] w-full flex-col items-start justify-center gap-2 rounded-2xl border border-border/70 bg-card/70 px-4 py-4 text-left text-foreground transition hover:border-gold hover:bg-gold/10";
            return t?.href ? (
              <Link key={n.label} to={t.href} className={cls}>
                {content}
              </Link>
            ) : (
              <button key={n.label} type="button" className={cls} onClick={() => openTheTool(n.tool)}>
                {content}
              </button>
            );
          })}
          </DepressionCarousel>
        </div>
      </section>

      {/* Open tool */}
      <div ref={toolRef} className="mt-8 scroll-mt-24 space-y-6">
        {openTool && INTERNAL_TOOLS.has(openTool) ? (
          <>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setOpenTool(null)}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden /> Close this tool
              </button>
              <button
                type="button"
                onClick={() => toggleFavourite(openTool)}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
                aria-pressed={favourites.includes(openTool)}
              >
                <Star
                  className={`h-4 w-4 ${favourites.includes(openTool) ? "fill-[var(--gold-deep)] text-[var(--gold-deep)]" : ""}`}
                  aria-hidden
                />
                {favourites.includes(openTool) ? "In my toolbox" : "Add to my toolbox"}
              </button>
            </div>
            {openTool === "tiny-step" ? <TinyStepTool {...toolProps} /> : null}
            {openTool === "meaning" ? <MeaningTool {...toolProps} /> : null}
            {openTool === "pleasure-experiment" ? <PleasureExperimentTool {...toolProps} /> : null}
            {openTool === "thought-reframe" ? <ThoughtReframeTool {...toolProps} /> : null}
            {openTool === "connection" ? <ConnectionTool {...toolProps} /> : null}
            {openTool === "anchors" ? <AnchorsTool {...toolProps} /> : null}
            {openTool === "patterns" ? <PatternsTool {...toolProps} /> : null}
            {openTool === "body-needs" ? <BodyNeedsTool {...toolProps} /> : null}
            {openTool === "body-symptoms" ? <BodySymptomTool {...toolProps} /> : null}
            {openTool === "emotion-wheel" ? <EmotionWheelTool {...toolProps} /> : null}
            {openTool === "cycle" ? <CycleTool {...toolProps} /> : null}
            {openTool === "motivation" ? <MotivationTool {...toolProps} /> : null}
          </>
        ) : null}
      </div>

      {/* My toolbox */}
      {favourites.length ? (
        <section className="mt-10">
          <h2 className="font-serif text-2xl text-foreground">My Depression Toolbox</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {favourites.map((slug) => {
              const t = TOOL_BY_SLUG[slug];
              if (!t) return null;
              return t.href ? (
                <LinkButton key={slug} to={t.href}>
                  {t.emoji} {t.title}
                </LinkButton>
              ) : (
                <SoftButton key={slug} onClick={() => openTheTool(slug)}>
                  {t.emoji} {t.title}
                </SoftButton>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Education & explorers */}
      <div className="mt-10 space-y-6">
        <UnderstandingDepression />
        <SymptomsExplorer hub={hub} />
        <TriggersExplorer hub={hub} />
        <CycleTool {...toolProps} />
        <BodyAndMood />
        <SleepAndMood />
        <GroundingShortcuts />
        <DepressionReflections hub={hub} />
        <TreatmentSupport />

        <ToolCard emoji="🧰" title="All Depression Tools">
          <DepressionCarousel label="All depression tools">
            {DEPRESSION_TOOLS.filter((t) => t.slug !== "crisis").map((t) => {
              const cls =
                "flex h-full min-h-[110px] flex-col items-start gap-2 rounded-2xl border border-border/70 bg-background/60 px-4 py-4 text-left transition hover:border-gold";
              const inner = (
                <>
                  <span aria-hidden className="text-xl">{t.emoji}</span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground">{t.title}</span>
                    <span className="block text-xs text-muted-foreground">{t.blurb}</span>
                  </span>
                </>
              );
              return t.href ? (
                <Link key={t.slug} to={t.href} className={cls}>
                  {inner}
                </Link>
              ) : (
                <button key={t.slug} type="button" className={cls} onClick={() => openTheTool(t.slug)}>
                  {inner}
                </button>
              );
            })}
          </DepressionCarousel>
        </ToolCard>
      </div>

      {/* Quick menu */}
      <section className="mt-10">
        <h2 className="font-serif text-2xl text-foreground">What Would Help Right Now?</h2>
        <div className="mt-3">
          <DepressionCarousel label="What would help right now">
          {[
            { label: "I want to understand what I'm feeling", emoji: "📖", action: () => openTheTool("emotion-wheel") },
            { label: "My thoughts won't stop", emoji: "💭", action: () => openTheTool("thought-reframe") },
            { label: "I have no motivation", emoji: "🌤️", action: () => openTheTool("tiny-step") },
            { label: "I feel overwhelmed right now", emoji: "🌍", action: () => openTheTool("grounding") },
          ].map((c) => (
            <button
              key={c.label}
              type="button"
              onClick={c.action}
              className="flex h-full min-h-[96px] w-full flex-col items-start justify-center gap-2 rounded-2xl border border-gold/30 bg-card/70 px-4 py-4 text-start text-sm font-medium text-foreground transition hover:border-gold hover:bg-gold/10"
            >
              <span aria-hidden className="text-xl">{c.emoji}</span>
              {c.label}
            </button>
          ))}
          </DepressionCarousel>
        </div>
        <div className="mt-4 rounded-3xl border border-border/70 bg-background/60 p-5">
          <SoftButton
            variant="primary"
            onClick={() =>
              setSuggestion(DONT_KNOW_SUGGESTIONS[Math.floor(Math.random() * DONT_KNOW_SUGGESTIONS.length)])
            }
          >
            I don't know what I need
          </SoftButton>
          {suggestion ? (
            <div className="mt-3">
              <p className="text-sm text-foreground">{suggestion}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <SoftButton onClick={() => setSuggestion(null)}>I'll try this</SoftButton>
                <SoftButton
                  onClick={() =>
                    setSuggestion(
                      DONT_KNOW_SUGGESTIONS[Math.floor(Math.random() * DONT_KNOW_SUGGESTIONS.length)],
                    )
                  }
                >
                  <RefreshCw className="h-4 w-4" aria-hidden /> Give me another
                </SoftButton>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      <section className="mt-8 rounded-3xl border border-gold/40 bg-gold/10 p-5">
        <h2 className="font-serif text-xl text-foreground">Support is always available</h2>
        <p className="mt-1 text-sm text-foreground/80">
          If things feel too heavy, you don't have to hold it alone.
        </p>
        <div className="mt-3">
          <LinkButton to="/crisis" variant="primary">
            <LifeBuoy className="h-4 w-4" aria-hidden /> Open Crisis Support
          </LinkButton>
        </div>
      </section>

      <Microcopy>
        Everything you save in this section is private to you and never appears in Community.
      </Microcopy>
    </div>
  );
}
